import React from 'react';
import { Shield, Sparkles, Leaf } from 'lucide-react';

export const Science = () => {
  return (
    <div className="grain-overlay" data-testid="science-page">
      {/* Hero */}
      <section className="py-24 px-6 lg:px-12 bg-purple-100 text-purple-900">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-mono uppercase tracking-[0.3em] mb-6 text-purple-600">
            The Science Behind
          </p>
          <h1 className="text-5xl md:text-6xl font-heading font-light mb-8">
            SAC® Formulation Technology
          </h1>
          <p className="text-xl leading-relaxed text-purple-700">
            Understanding the revolutionary calcium ion delivery system that transforms healing.
          </p>
        </div>
      </section>

      {/* Core Ingredients */}
      <section className="py-24 px-6 lg:px-12 bg-purple-25">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-heading font-light text-center mb-16">
            Core Ingredients
          </h2>
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                <Shield className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-2xl font-heading mb-4">Selenium</h3>
              <p className="text-stone-600 leading-relaxed">
                A powerful antioxidant that reduces oxidative stress and protects cells from damage caused by free radicals.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                <Sparkles className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-2xl font-heading mb-4">Calcium (SAC® Formula)</h3>
              <p className="text-stone-600 leading-relaxed">
                Essential for bone strength and overall cell health. Our SAC® formulation ensures optimal absorption and utilization.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                <Leaf className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-2xl font-heading mb-4">Magnesium</h3>
              <p className="text-stone-600 leading-relaxed">
                Crucial for bone formation and cardiovascular health. Works synergistically with calcium for optimal results.
              </p>
            </div>
          </div>

          {/* SAC Technology Explanation */}
          <div className="max-w-4xl mx-auto mt-16 bg-white p-8 md:p-12 rounded-2xl shadow-lg">
            <h3 className="text-3xl font-heading font-light text-purple-900 mb-6 text-center">
              What is SAC® technology?
            </h3>
            <p className="text-lg text-purple-800 leading-relaxed">
              <strong>Sigma Anti-bonding Calcium (SAC®)</strong> is a revolutionary technological advancement that is changing lives with its passive calcium delivery system. While other forms of calcium delivery struggle in areas of absorption, SAC® safely increases bone density naturally by awakening the body's own bone-formation process.
            </p>
          </div>
        </div>
      </section>

      {/* The Process */}
      <section className="py-24 px-6 lg:px-12 bg-purple-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-heading font-light text-center mb-16">
            Our Exclusive Process
          </h2>
          
          <div className="space-y-12">
            <div>
              <h3 className="text-2xl font-heading mb-4">1. Source Selection</h3>
              <p className="text-stone-600 leading-relaxed">
                We begin with premium oyster shells, carefully selected for their purity and mineral content.
                These shells contain the perfect matrix for calcium ion formation.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-heading mb-4">2. Hot & Cold Treatment</h3>
              <p className="text-stone-600 leading-relaxed">
                Our exclusive hot and cold manufacturing process destroys heavy metals, toxins, and impurities
                while preserving the beneficial minerals. This proprietary method sets our products apart.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-heading mb-4">3. Ion Formation</h3>
              <p className="text-stone-600 leading-relaxed">
                The SAC® formulation technology transforms calcium into its ionic form, creating the world's first
                safe calcium ion delivery system. These ions are readily absorbed and utilized by the body.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-heading mb-4">4. Quality Assurance</h3>
              <p className="text-stone-600 leading-relaxed">
                Every batch undergoes rigorous testing to ensure purity, potency, and safety. We maintain
                the highest standards in the industry for natural healing products.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 px-6 lg:px-12 bg-purple-25">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-heading font-light text-center mb-16">
            Clinical Benefits
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-stone-100 p-8 rounded-2xl">
              <h3 className="text-xl font-heading mb-3">Enhanced Absorption</h3>
              <p className="text-stone-600">
                Calcium ions are absorbed up to 10x more efficiently than traditional calcium supplements.
              </p>
            </div>

            <div className="bg-stone-100 p-8 rounded-2xl">
              <h3 className="text-xl font-heading mb-3">Cellular Detoxification</h3>
              <p className="text-stone-600">
                Helps remove harmful substances and heavy metals from the body at the cellular level.
              </p>
            </div>

            <div className="bg-stone-100 p-8 rounded-2xl">
              <h3 className="text-xl font-heading mb-3">Bone Health</h3>
              <p className="text-stone-600">
                Supports bone density and strength, reducing the risk of osteoporosis and fractures.
              </p>
            </div>

            <div className="bg-stone-100 p-8 rounded-2xl">
              <h3 className="text-xl font-heading mb-3">Oxidative Stress Reduction</h3>
              <p className="text-stone-600">
                Powerful antioxidants work to neutralize free radicals and reduce oxidative damage.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};